import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { C } from '../theme'

const CSS = `
@keyframes lwFlash { 0%,100%{background:#fff1f2} 50%{background:#ffe4e6} }
@keyframes lwWarn  { 0%,100%{background:#fffbeb} 50%{background:#fef3c7} }
`

function TerminBadge({ wunschdatum }) {
  if (!wunschdatum) return <span style={{ color: '#ccc' }}>–</span>
  const diff = Math.floor((new Date(wunschdatum) - new Date()) / 86400000)
  const datum = new Date(wunschdatum).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
  if (diff < 0)  return <span style={bs('#c53030')}>⚠ {Math.abs(diff)}T ÜBER · {datum}</span>
  if (diff === 0) return <span style={bs('#ef4444')}>🔴 HEUTE · {datum}</span>
  if (diff === 1) return <span style={bs('#f59e0b')}>⏰ MORGEN · {datum}</span>
  if (diff <= 3)  return <span style={bs('#f59e0b')}>{diff}T · {datum}</span>
  return <span style={bs('#e2e8f0', '#4a5568')}>{datum}</span>
}

export default function Leihwagen({ userRole, isMobile }) {
  const [flotte, setFlotte]         = useState([])
  const [fahrzeugMap, setFahrzeugMap] = useState({})
  const [form, setForm]             = useState({ kennzeichen: '', modell: '' })
  const [editId, setEditId]         = useState(null)
  const [editData, setEditData]     = useState({ kennzeichen: '', modell: '' })
  const [loading, setLoading]       = useState(false)
  const [error, setError]           = useState('')

  useEffect(() => {
    loadFlotte()
    loadFahrzeuge()
    const ch1 = supabase.channel('leihwagen-rt').on('postgres_changes', { event: '*', schema: 'public', table: 'leihwagen' }, () => loadFlotte()).subscribe()
    const ch2 = supabase.channel('lw-fz-rt').on('postgres_changes', { event: '*', schema: 'public', table: 'fahrzeuge' }, () => loadFahrzeuge()).subscribe()
    return () => { supabase.removeChannel(ch1); supabase.removeChannel(ch2) }
  }, [])

  const loadFlotte = async () => {
    const { data } = await supabase.from('leihwagen').select('*').order('kennzeichen', { ascending: true })
    setFlotte(data || [])
  }

  const loadFahrzeuge = async () => {
    const { data } = await supabase.from('fahrzeuge').select('id, kennzeichen, kunde, wunschdatum, leihwagen_id').not('leihwagen_id', 'is', null)
    const map = {}
    ;(data || []).forEach(f => { map[f.leihwagen_id] = f })
    setFahrzeugMap(map)
  }

  const hinzufuegen = async (e) => {
    e.preventDefault()
    setError('')
    if (!form.kennzeichen) { setError('Kennzeichen erforderlich'); return }
    if (flotte.find(f => f.kennzeichen.toLowerCase() === form.kennzeichen.toLowerCase())) { setError('Kennzeichen bereits vorhanden!'); return }
    setLoading(true)
    await supabase.from('leihwagen').insert({ kennzeichen: form.kennzeichen.toUpperCase(), modell: form.modell, verfügbar: true })
    setForm({ kennzeichen: '', modell: '' })
    setLoading(false)
  }

  const editSpeichern = async (id) => {
    await supabase.from('leihwagen').update({ kennzeichen: editData.kennzeichen.toUpperCase(), modell: editData.modell }).eq('id', id)
    setEditId(null)
  }

  const toggleVerfuegbar = async (lw) => {
    if (lw.verfügbar) {
      const an = window.prompt('Vermietet an (Name des Kunden)?')
      if (!an) return
      await supabase.from('leihwagen').update({ verfügbar: false, vergeben_an: an }).eq('id', lw.id)
    } else {
      await supabase.from('leihwagen').update({ verfügbar: true, vergeben_an: null }).eq('id', lw.id)
    }
  }

  const loeschen = async (id) => {
    if (!window.confirm('Fahrzeug wirklich löschen?')) return
    await supabase.from('leihwagen').delete().eq('id', id)
  }

  const verfuegbar = flotte.filter(f => f.verfügbar)
  const vergeben   = flotte.filter(f => !f.verfügbar)

  // Alert: vergeben + termin naht
  const alertList = vergeben.filter(lw => {
    const fz = fahrzeugMap[lw.id]
    if (!fz?.wunschdatum) return false
    return Math.floor((new Date(fz.wunschdatum) - new Date()) / 86400000) <= 1
  })

  return (
    <div style={{ padding: 24 }}>
      <style>{CSS}</style>

      {/* Alert Banner */}
      {alertList.length > 0 && (
        <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: '#be123c', fontSize: 13 }}>⚠ Rückgabe fällig:</span>
          {alertList.map(lw => {
            const fz = fahrzeugMap[lw.id]
            const diff = Math.floor((new Date(fz.wunschdatum) - new Date()) / 86400000)
            return (
              <span key={lw.id} style={{ background: diff < 0 ? '#be123c' : '#f59e0b', color: '#fff', borderRadius: 5, padding: '2px 9px', fontSize: 11, fontWeight: 700 }}>
                {lw.kennzeichen} → {fz.kennzeichen} {diff < 0 ? `(${Math.abs(diff)}T überfällig)` : diff === 0 ? '(HEUTE)' : '(MORGEN)'}
              </span>
            )
          })}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Leihwagen-Flotte</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={badge('#c6f6d5', '#276749')}>✓ {verfuegbar.length} frei</span>
          <span style={badge('#fed7d7', '#c53030')}>✗ {vergeben.length} vermietet</span>
          <span style={badge('#e2e8f0', '#4a5568')}>{flotte.length} gesamt</span>
        </div>
      </div>

      {/* Add Form */}
      {userRole === 'admin' && (
        <form onSubmit={hinzufuegen} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
          <input style={inp} placeholder="Kennzeichen" value={form.kennzeichen} onChange={e => setForm({ ...form, kennzeichen: e.target.value })} />
          <input style={inp} placeholder="Modell" value={form.modell} onChange={e => setForm({ ...form, modell: e.target.value })} />
          <button style={{ padding: '8px 16px', background: C.primary, color: C.onPrimary, border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 13 }} type="submit" disabled={loading}>+ Hinzufügen</button>
          {error && <span style={{ color: '#dc2626', fontSize: 12 }}>{error}</span>}
        </form>
      )}

      {/* Mobile: Kartenliste */}
      {isMobile && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {flotte.map(lw => {
            const fz = fahrzeugMap[lw.id]
            return (
              <div key={lw.id} style={{
                background: '#fff', borderRadius: 10, padding: '12px 14px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                borderLeft: `4px solid ${lw.verfügbar ? '#48bb78' : C.danger}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontWeight: 800, fontSize: 15 }}>{lw.kennzeichen}</div>
                  <span style={{ color: lw.verfügbar ? '#276749' : '#c53030', fontWeight: 700, fontSize: 12 }}>
                    {lw.verfügbar ? '✓ Frei' : '✗ Vermietet'}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{lw.modell || '–'}</div>
                {!lw.verfügbar && (
                  <div style={{ fontSize: 12, marginTop: 5 }}>
                    {lw.vergeben_an && <span style={{ color: '#374151' }}>An: <strong>{lw.vergeben_an}</strong></span>}
                    {fz && <span style={{ marginLeft: 8 }}><TerminBadge wunschdatum={fz.wunschdatum} /></span>}
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <button style={{ ...actionBtn, background: lw.verfügbar ? '#dc2626' : '#48bb78', padding: '8px 14px' }} onClick={() => toggleVerfuegbar(lw)}>
                    {lw.verfügbar ? 'Vermieten' : 'Rückgabe'}
                  </button>
                </div>
              </div>
            )
          })}
          {flotte.length === 0 && <p style={{ color: '#999', textAlign: 'center', padding: 40 }}>Noch keine Fahrzeuge in der Flotte</p>}
        </div>
      )}

      {/* Desktop: Tabelle */}
      <div style={{ borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.07)', display: isMobile ? 'none' : 'block' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {['Leihwagen','Modell','Status','Vermietet an','Fahrzeug (Kunde)','Rückgabe Termin',''].map(h => (
                <th key={h} style={{ background: C.dark, color: '#fff', padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, borderBottom: `3px solid ${C.primary}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {flotte.map((lw, i) => {
              const fz = fahrzeugMap[lw.id]
              const diff = fz?.wunschdatum ? Math.floor((new Date(fz.wunschdatum) - new Date()) / 86400000) : null
              const rowAnim = diff !== null && diff < 0 ? 'lwFlash 1.5s infinite' : diff !== null && diff <= 1 ? 'lwWarn 2s infinite' : 'none'
              const rowBg   = lw.verfügbar ? '#f0fff4' : diff !== null && diff < 0 ? '#fff1f2' : diff !== null && diff <= 1 ? '#fffbeb' : '#fff5f5'

              return (
                <tr key={lw.id} style={{ background: rowBg, animation: rowAnim }}>
                  <td style={td}>
                    {editId === lw.id
                      ? <input style={{ ...inp, padding: '4px 8px', width: 110 }} value={editData.kennzeichen} onChange={e => setEditData({ ...editData, kennzeichen: e.target.value })} />
                      : <strong>{lw.kennzeichen}</strong>}
                  </td>
                  <td style={td}>
                    {editId === lw.id
                      ? <input style={{ ...inp, padding: '4px 8px', width: 130 }} value={editData.modell} onChange={e => setEditData({ ...editData, modell: e.target.value })} />
                      : lw.modell || '–'}
                  </td>
                  <td style={td}>
                    <span style={{ color: lw.verfügbar ? '#276749' : '#c53030', fontWeight: 700 }}>
                      {lw.verfügbar ? '✓ Frei' : '✗ Vermietet'}
                    </span>
                  </td>
                  <td style={td}>{lw.vergeben_an || <span style={{ color: '#ccc' }}>–</span>}</td>
                  <td style={td}>
                    {fz ? (
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{fz.kennzeichen}</div>
                        {fz.kunde && <div style={{ fontSize: 11, color: '#888' }}>{fz.kunde}</div>}
                      </div>
                    ) : <span style={{ color: '#ccc' }}>–</span>}
                  </td>
                  <td style={td}><TerminBadge wunschdatum={fz?.wunschdatum} /></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {editId === lw.id ? (
                      <>
                        <button style={saveBtn} onClick={() => editSpeichern(lw.id)}>✓</button>
                        <button style={cancelBtn} onClick={() => setEditId(null)}>✕</button>
                      </>
                    ) : (
                      <>
                        <button style={{ ...actionBtn, background: lw.verfügbar ? '#dc2626' : '#48bb78' }} onClick={() => toggleVerfuegbar(lw)}>
                          {lw.verfügbar ? 'Vermieten' : 'Rückgabe'}
                        </button>
                        {userRole === 'admin' && (
                          <>
                            <button style={{ ...actionBtn, background: '#4299e1', marginLeft: 4 }}
                              onClick={() => { setEditId(lw.id); setEditData({ kennzeichen: lw.kennzeichen, modell: lw.modell || '' }) }}>✏</button>
                            <button style={{ ...actionBtn, background: '#718096', marginLeft: 4 }} onClick={() => loeschen(lw.id)}>🗑</button>
                          </>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {flotte.length === 0 && <p style={{ color: '#999', textAlign: 'center', padding: 40 }}>Noch keine Fahrzeuge in der Flotte</p>}
      </div>
    </div>
  )
}

const badge     = (bg, color) => ({ background: bg, color, borderRadius: 6, padding: '4px 10px', fontSize: 13, fontWeight: 600 })
const bs        = (bg, color = '#fff') => ({ background: bg, color, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 })
const inp       = { padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }
const td        = { padding: '9px 12px', borderBottom: '1px solid #e2e8f0', verticalAlign: 'middle' }
const actionBtn = { padding: '5px 10px', border: 'none', borderRadius: 5, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 12 }
const saveBtn   = { padding: '5px 10px', background: '#48bb78', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', marginRight: 4 }
const cancelBtn = { padding: '5px 10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' }
